import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table, Button } from 'reactstrap';
import { AppContext } from '../application/provider';
import AttendanceService from '../axios_services/attendance.service';
import Loader from '../components/Loader/Loader';
import { ButtonSearch, ButtonUpdate } from '../components/Buttons/Buttons';
import ModalObservaciones from '../components/Modal/ModalObservaciones';
import ModalPerfilesChecklist from '../components/Modal/ModalPerfilesChecklist';
import ModalConfiguracionAsistencia from '../components/Modal/ModalConfiguracionAsistencia';

import { ListChecks, UserCog, Settings } from 'lucide-react';
import './Asistencias.css';

const formatTime = (t) => {
    if (!t || typeof t !== 'string') return '';
    let time = t;
    if (t.includes(' ')) time = t.split(' ')[1];
    else if (t.includes('T')) time = t.split('T')[1];
    return time.slice(0, 5);
};

const TimeSelector = ({ value, onChange, onBlur, disabled }) => {
    const displayValue = formatTime(value);

    return (
        <div className="time-selector-container">
            <input
                type="time"
                className="time-select-24h"
                value={displayValue}
                onChange={e => onChange(e.target.value)}
                onBlur={e => onBlur && onBlur(e.target.value)}
                disabled={disabled}
            />
        </div>
    );
};


function Asistencias() {
    const [state] = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);
    const [registros, setRegistros] = useState([]);
    const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
    const [areaFilter, setAreaFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('1'); // 1: Activo, 0: Cesado
    const [eligibleUsers, setEligibleUsers] = useState([]);
    const [obsModal, setObsModal] = useState({
        isOpen: false,
        userId: null,
        nombreColaborador: '',
        text: '',
        estado: 1,
        aplicaTardanza: 0,
        aplicaTardanza1: 0,
        aplicaTardanza2: 0,
        justificado1: 0,
        justificado2: 0,
        inasistencia1: 0,
        inasistencia2: 0,
        justificado_salida1: 0,
        justificado_salida2: 0,
        hasSecondShift: false
    });
    const [perfilesModal, setPerfilesModal] = useState(false);
    const [configAsistenciaModal, setConfigAsistenciaModal] = useState(false);
    const [frecuencia, setFrecuencia] = useState('diaria');
    const [frecuenciaActiva, setFrecuenciaActiva] = useState('diaria');
    const [mesSeleccionado, setMesSeleccionado] = useState(() => new Date().toISOString().slice(0, 7));
    const [registrosMensuales, setRegistrosMensuales] = useState({ users: [], records: {} });

    const initialFetchDone = useRef(false);

    const allowedEditors = [5, 8, 22, 26];
    const currentPerfil = Number(state.user?.perfil_id);
    const canEditAny = allowedEditors.includes(currentPerfil);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const resp = await AttendanceService.getEligibleUsers({ fecha, status: statusFilter });
            setEligibleUsers(resp.data || []);
            await loadData(resp.data);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isLocked = (() => {
        if (!fecha) return false;
        const targetDate = new Date(fecha + 'T00:00:00');
        const diffTime = today - targetDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 60;
    })();

    const isFuture = (() => {
        if (!fecha) return false;
        const targetDate = new Date(fecha + 'T00:00:00');
        return targetDate > today;
    })();

    const isEditingDisabled = isLocked || isFuture;

    const extractTimeForCompare = (t) => {
        if (t == null || t === '') return '';
        const s = String(t);
        if (s.includes(' ')) return s.split(' ')[1].slice(0, 8);
        if (s.includes('T')) return s.split('T')[1].slice(0, 8);
        return s.length >= 8 ? s.slice(0, 8) : (s.length === 5 ? `${s}:00` : s);
    };

    const scheduleFromRecord = (found) => {
        if (!found || found.scheduled_entrada1 == null || found.scheduled_entrada1 === '') return null;
        return [
            extractTimeForCompare(found.scheduled_entrada1),
            found.scheduled_salida1 != null ? extractTimeForCompare(found.scheduled_salida1) : null,
            found.scheduled_entrada2 != null ? extractTimeForCompare(found.scheduled_entrada2) : null,
            found.scheduled_salida2 != null ? extractTimeForCompare(found.scheduled_salida2) : null,
        ];
    };

    const resolveSchedule = (found, u, dow) => {
        const fromDb = scheduleFromRecord(found);
        if (fromDb) return fromDb;

        const daySuffixes = { 0: 'd', 1: 'l', 2: 'm', 3: 'mi', 4: 'j', 5: 'v', 6: 's' };
        const suffix = daySuffixes[dow];
        if (suffix && u[`config_entrada1_${suffix}`]) {
            return [
                u[`config_entrada1_${suffix}`] || null,
                u[`config_salida1_${suffix}`] || null,
                u[`config_entrada2_${suffix}`] || null,
                u[`config_salida2_${suffix}`] || null,
            ];
        }

        const isSat = dow === 6;
        if (isSat) {
            return [
                u.config_entrada1_s || null,
                u.config_salida1_s || null,
                u.config_entrada2_s || null,
                u.config_salida2_s || null,
            ];
        } else {
            return [
                u.config_entrada1_lv || null,
                u.config_salida1_lv || null,
                u.config_entrada2_lv || null,
                u.config_salida2_lv || null,
            ];
        }
    };

    const formatHHMM = (t) => {
        if (!t) return '';
        const x = String(t);
        return x.length >= 5 ? x.slice(0, 5) : x;
    };

    const horarioResumen = (sch, macroAreaId) => {
        if (sch[0] === null) return '⚠️ Falta asignar un horario';
        const a = formatTime24h(sch[0]);
        const b = formatTime24h(sch[1]);
        const c = formatTime24h(sch[2]);
        const d = formatTime24h(sch[3]);
        if (Number(macroAreaId) === 1) return `${a}–${b} · ${c}–${d}`;
        if (sch[2] && sch[3]) return `${a}–${b} · ${c}–${d}`;
        return `${a}–${b}`;
    };

    const formatTime24h = (time24) => {
        if (!time24) return '—';
        const parts = time24.split(':');
        if (parts.length < 2) return time24;
        return `${parts[0].padStart(2, '0')}:${parts[1].substring(0, 2)}`;
    };

    const loadData = async (usersList = null) => {
        setIsLoading(true);
        try {
            if (frecuencia === 'diaria') {
                setFrecuenciaActiva('diaria');
                let listToUse = usersList;
                if (!listToUse) {
                    const eligResp = await AttendanceService.getEligibleUsers({ fecha, status: statusFilter });
                    listToUse = eligResp.data || [];
                    setEligibleUsers(listToUse);
                }
                const resp = await AttendanceService.getByDate(fecha);
                const records = resp.data || [];
                const dow = new Date(fecha + 'T00:00:00').getDay();

                const filteredUsers = listToUse.filter(u => {
                    if (!areaFilter) return true;
                    return Number(u.macro_area_id) === Number(areaFilter);
                });

                const merged = filteredUsers.map(u => {
                    const found = records.find(r => Number(r.usuario_id) === Number(u.usuario_id));
                    const sch = resolveSchedule(found, u, dow);
                    const hasSecond = !!(sch[2] && sch[3]);

                    return {
                        usuario_id: u.usuario_id,
                        nombre_completo: u.nombre_completo,
                        fecha: fecha,
                        scheduled_entrada1: sch[0],
                        scheduled_salida1: sch[1],
                        scheduled_entrada2: sch[2],
                        scheduled_salida2: sch[3],
                        entrada1: (found && found.entrada1) ? formatTime(found.entrada1) : (sch[0] ? formatHHMM(sch[0]) : ''),
                        salida1: (found && found.salida1) ? formatTime(found.salida1) : (sch[1] ? formatHHMM(sch[1]) : ''),
                        entrada2: (found && found.entrada2 && hasSecond) ? formatTime(found.entrada2) : (hasSecond && sch[2] ? formatHHMM(sch[2]) : ''),
                        salida2: (found && found.salida2 && hasSecond) ? formatTime(found.salida2) : (hasSecond && sch[3] ? formatHHMM(sch[3]) : ''),
                        estado: found ? Number(found.estado) : 1,
                        aplica_tardanza: found ? Number(found.aplica_tardanza || 0) : 0,
                        aplica_tardanza1: found ? Number(found.aplica_tardanza1 || 0) : 0,
                        aplica_tardanza2: found ? Number(found.aplica_tardanza2 || 0) : 0,
                        justificado1: found ? Number(found.justificado1 || 0) : 0,
                        justificado2: found ? Number(found.justificado2 || 0) : 0,
                        inasistencia1: found ? Number(found.inasistencia1 || 0) : 0,
                        inasistencia2: found ? Number(found.inasistencia2 || 0) : 0,
                        justificado_salida1: found ? Number(found.justificado_salida1 || 0) : 0,
                        justificado_salida2: found ? Number(found.justificado_salida2 || 0) : 0,
                        observaciones: found ? found.observaciones : '',
                        id: found ? found.id : null,
                        fromDb: !!found,
                        hasSecondBlock: hasSecond,
                        horarioResumen: horarioResumen(sch, u.macro_area_id),
                        tolerancia: Number(u.config_tolerancia || 5)
                    };
                });
                setRegistros(merged);
            } else {
                setFrecuenciaActiva(frecuencia);
                const [year, month] = mesSeleccionado.split('-');
                const resp = await AttendanceService.getMonthlyReport(parseInt(month), parseInt(year), areaFilter || null, statusFilter);
                setRegistrosMensuales(resp.data || { users: [], records: {} });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const getDaysInMonth = (year, month) => {
        return new Date(year, month, 0).getDate();
    };

    const renderTableMensual = () => {
        const [year, month] = mesSeleccionado.split('-').map(v => parseInt(v));

        const range = registrosMensuales.range;
        const days = [];
        
        if (range && range.start && range.end) {
            let curr = new Date(range.start + 'T00:00:00');
            const end = new Date(range.end + 'T00:00:00');
            while (curr <= end) {
                days.push({ day: curr.getDate(), month: curr.getMonth() + 1, year: curr.getFullYear() });
                curr.setDate(curr.getDate() + 1);
            }
        } else {
            // Fallback en caso de que no haya rango
            const prevMonth = month === 1 ? 12 : month - 1;
            const prevYear = month === 1 ? year - 1 : year;
            const daysInPrevMonth = new Date(prevYear, prevMonth, 0).getDate();
            for (let d = 26; d <= daysInPrevMonth; d++) {
                days.push({ day: d, month: prevMonth, year: prevYear });
            }
            for (let d = 1; d <= 25; d++) {
                days.push({ day: d, month: month, year: year });
            }
        }

        const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

        const stickyThStyle = {
            position: 'sticky',
            left: 0,
            zIndex: 14,
            boxShadow: '1px 0 0 0 #adadad',
            minWidth: '180px',
            textAlign: 'center',
            backgroundColor: 'var(--azul-oscuro)',
            color: 'white',
        };

        const stickyTdStyle = {
            position: 'sticky',
            left: 0,
            zIndex: 5,
            backgroundColor: '#fff',
            whiteSpace: 'nowrap',
            boxShadow: '1px 0 0 0 #adadad',
        };

        return (
            <div className="table-container" style={{ marginTop: '15px', maxHeight: '72vh' }}>
                <table style={{ width: 'max-content', minWidth: '100%' }}>
                    <thead>
                        <tr>
                            <th rowSpan={3} style={stickyThStyle}>
                                COLABORADOR
                            </th>
                            {days.map((item, idx) => {
                                const dow = new Date(item.year, item.month - 1, item.day).getDay();
                                const isSunday = dow === 0;
                                const dayPadded = String(item.day).padStart(2, '0');
                                return (
                                    <th
                                        key={idx}
                                        colSpan={4}
                                        style={{
                                            textAlign: 'center',
                                            minWidth: '240px',
                                            opacity: isSunday ? 0.65 : 1,
                                        }}
                                    >
                                        {DIAS_SEMANA[dow]} {dayPadded}
                                    </th>
                                );
                            })}
                        </tr>
                        <tr>
                            {days.map((_, idx) => {
                                // const dow = new Date(item.year, item.month - 1, item.day).getDay();
                                // const isSunday = dow === 0;
                                return [
                                    <th key={`${idx}-t1`} colSpan={2} style={{ textAlign: 'center' }}>TURNO I</th>,
                                    <th key={`${idx}-t2`} colSpan={2} style={{ textAlign: 'center' }}>TURNO II</th>,
                                ];
                            })}
                        </tr>
                        <tr>
                            {days.map((_, idx) => {
                                return [
                                    <th key={`${idx}-ing1`} style={{ textAlign: 'center' }}>INGRESO</th>,
                                    <th key={`${idx}-sal1`} style={{ textAlign: 'center' }}>SALIDA</th>,
                                    <th key={`${idx}-ing2`} style={{ textAlign: 'center' }}>INGRESO</th>,
                                    <th key={`${idx}-sal2`} style={{ textAlign: 'center' }}>SALIDA</th>,
                                ];
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {registrosMensuales.users && registrosMensuales.users.length > 0 ? registrosMensuales.users.map((u, idx) => {
                            const rowBg = idx % 2 === 0 ? '#ffffff' : '#f9fafb';
                            return (
                                <tr key={u.usuario_id}>
                                    <td style={{ ...stickyTdStyle, backgroundColor: rowBg }}>
                                        {u.nombre_completo.toUpperCase()}
                                    </td>
                                    {days.map((item, dIdx) => {
                                        const dayPadded = String(item.day).padStart(2, '0');
                                        const dateStr = `${item.year}-${String(item.month).padStart(2, '0')}-${dayPadded}`;
                                        const userRecords = registrosMensuales.records[u.usuario_id] || [];
                                        const rec = userRecords.find(r => r.fecha === dateStr || (r.fecha && r.fecha.startsWith(dateStr)));

                                        const isFaltaGral = rec && Number(rec.estado) === 2;
                                        const isT1Falta = rec && (Number(rec.inasistencia1) === 1 || (isFaltaGral && Number(rec.inasistencia1) === 0 && Number(rec.inasistencia2) === 0));
                                        const isT2Falta = rec && (Number(rec.inasistencia2) === 1 || (isFaltaGral && Number(rec.inasistencia1) === 0 && Number(rec.inasistencia2) === 0));

                                        const i1 = (rec && rec.entrada1 && !isT1Falta) ? formatTime(rec.entrada1) : (isT1Falta ? '—' : '—');
                                        const s1 = (rec && rec.salida1 && !isT1Falta) ? formatTime(rec.salida1) : '—';
                                        const i2 = (rec && rec.entrada2 && !isT2Falta) ? formatTime(rec.entrada2) : (isT2Falta ? '—' : '—');
                                        const s2 = (rec && rec.salida2 && !isT2Falta) ? formatTime(rec.salida2) : '—';

                                        const hasRecord = !!rec;
                                        const dow = new Date(item.year, item.month - 1, item.day).getDay();
                                        const isSunday = dow === 0;

                                        const getMin = (t) => {
                                            if (!t) return null;
                                            const s = String(t);
                                            const part = s.includes(' ') ? s.split(' ')[1] : s.includes('T') ? s.split('T')[1] : s;
                                            const [h, m] = part.slice(0, 5).split(':').map(Number);
                                            return isNaN(h) || isNaN(m) ? null : h * 60 + m;
                                        };

                                        const est = rec ? Number(rec.estado) : 1;
                                        const tol = Number(u.config_tolerancia || 5);

                                        const getCellBg = (isIngreso, isTurno2) => {
                                            if (!rec) return isSunday ? '#f0f7fc' : rowBg;

                                            const isTurnoFalta = isTurno2 ? isT2Falta : isT1Falta;
                                            if (isTurnoFalta) return '#fee2e2';
                                            if (est === 3) return '#e0f2fe';

                                            if (isIngreso) {
                                                const ent = isTurno2 ? rec.entrada2 : rec.entrada1;
                                                const sch = isTurno2 ? rec.scheduled_entrada2 : rec.scheduled_entrada1;
                                                const isJustif = isTurno2 ? Number(rec.justificado2) === 1 : Number(rec.justificado1) === 1;

                                                if (ent && sch) {
                                                    const e = getMin(ent);
                                                    const s = getMin(sch);
                                                    if (e !== null && s !== null && e > s) {
                                                        if (isJustif || est === 4) return '#e0f2fe';
                                                        if (e - s <= tol) return '#fef9c3';
                                                        return '#ffedd5';
                                                    }
                                                }
                                            } else {
                                                const sal = isTurno2 ? rec.salida2 : rec.salida1;
                                                const sch = isTurno2 ? rec.scheduled_salida2 : rec.scheduled_salida1;
                                                const isJustifSal = isTurno2 ? Number(rec.justificado_salida2) === 1 : Number(rec.justificado_salida1) === 1;

                                                if (sal && sch) {
                                                    const sReal = getMin(sal);
                                                    const sSch = getMin(sch);
                                                    if (sReal !== null && sSch !== null && sReal < sSch) {
                                                        if (isJustifSal) return '#e0f2fe';
                                                        return '#ffedd5';
                                                    }
                                                }
                                            }

                                            return isSunday ? '#f0f7fc' : rowBg;
                                        };

                                        const tdStyle = {
                                            textAlign: 'center',
                                            color: hasRecord ? '#333' : '#bbb',
                                            minWidth: '60px',
                                        };

                                        return [
                                            <td key={`${dIdx}-i1`} style={{ ...tdStyle, backgroundColor: getCellBg(true, false) }}>{i1}</td>,
                                            <td key={`${dIdx}-s1`} style={{ ...tdStyle, backgroundColor: getCellBg(false, false) }}>{s1}</td>,
                                            <td key={`${dIdx}-i2`} style={{ ...tdStyle, backgroundColor: getCellBg(true, true), color: hasRecord && rec.entrada2 ? '#5a2d82' : '#bbb' }}>{i2}</td>,
                                            <td key={`${dIdx}-s2`} style={{ ...tdStyle, backgroundColor: getCellBg(false, true), color: hasRecord && rec.salida2 ? '#5a2d82' : '#bbb' }}>{s2}</td>,
                                        ];
                                    })}
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan={(days.length * 4) + 1} className="sin-data">
                                    No hay datos disponibles para este período.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const handleCellChange = (userId, field, value) => {
        setRegistros(prev => prev.map(r => r.usuario_id === userId ? { ...r, [field]: value } : r));
    };

    const saveRow = async (fullRow) => {
        if (isEditingDisabled) {
            const msg = isFuture ? "⚠️ No se pueden registrar asistencias en fechas futuras." : "⚠️ Esta fecha está bloqueada para edición (más de 60 días de antigüedad).";
            alert(msg);
            return;
        }

        const row = fullRow;
        if (row.scheduled_entrada1 === null) {
            alert("⚠️ No se puede registrar la asistencia porque este colaborador no tiene un horario asignado. Por favor, configure su horario primero.");
            return;
        }

        const userId = row.usuario_id;
        setIsLoading(true);

        const makeTime = (time) => (time && time.length === 5) ? `${fecha} ${time}:00` : null;

        const payload = {
            usuario_id: userId,
            fecha: fecha,
            entrada1: makeTime(row.entrada1),
            salida1: makeTime(row.salida1),
            entrada2: row.hasSecondBlock ? makeTime(row.entrada2) : null,
            salida2: row.hasSecondBlock ? makeTime(row.salida2) : null,
            estado: row.estado,
            aplica_tardanza: row.aplica_tardanza || 0,
            aplica_tardanza1: row.aplica_tardanza1 || 0,
            aplica_tardanza2: row.aplica_tardanza2 || 0,
            justificado1: row.justificado1 || 0,
            justificado2: row.justificado2 || 0,
            inasistencia1: row.inasistencia1 || 0,
            inasistencia2: row.inasistencia2 || 0,
            justificado_salida1: row.justificado_salida1 || 0,
            justificado_salida2: row.justificado_salida2 || 0,
            observaciones: row.observaciones
        };

        try {
            const resp = await AttendanceService.update(payload);
            setRegistros(prev => prev.map(r => {
                if (r.usuario_id !== userId) return r;
                const d = resp.data;
                let next = {
                    ...r,
                    ...d,
                    entrada1: d.entrada1 ? formatTime(d.entrada1) : r.entrada1,
                    salida1: d.salida1 ? formatTime(d.salida1) : r.salida1,
                    entrada2: d.entrada2 ? formatTime(d.entrada2) : r.entrada2,
                    salida2: d.salida2 ? formatTime(d.salida2) : r.salida2,
                    fromDb: true
                };
                if (d && d.scheduled_entrada1) {
                    const sch = scheduleFromRecord(d);
                    if (sch) {
                        next.scheduled_entrada1 = sch[0];
                        next.scheduled_salida1 = sch[1];
                        next.scheduled_entrada2 = sch[2];
                        next.scheduled_salida2 = sch[3];
                        next.hasSecondBlock = !!(sch[2] && sch[3]);
                    }
                }
                return next;
            }));
        } catch (e) {
            console.error('Error saving row:', e);
            alert('Error al guardar cambios.');
        } finally {
            setIsLoading(false);
        }
    };

    const isShiftAbsent = (r, shift) => {
        const inas = Number(r[`inasistencia${shift}`] || 0);
        if (inas > 0) return true;
        // legacy fallback
        const hasNewFlags = Number(r.inasistencia1 || 0) > 0 || Number(r.inasistencia2 || 0) > 0;
        if (!hasNewFlags && (Number(r.estado) === 2 || Number(r.estado) === 3)) {
            if (shift === 2 && !r.hasSecondBlock) return false;
            return true;
        }
        return false;
    };

    const getShiftStyle = (r, shift) => {
        if (!r.fromDb) return {};

        const inas = Number(r[`inasistencia${shift}`] || 0);
        if (inas === 1) return { backgroundColor: '#fee2e2' };
        if (inas === 2) return { backgroundColor: '#e0f2fe' };

        const just = Number(r[`justificado${shift}`] || 0);
        const apl = Number(r[`aplica_tardanza${shift}`] || 0) || Number(r.aplica_tardanza || 0);
        if (just === 1 || apl === 1) return { backgroundColor: '#e0f2fe' };

        // Legacy fallback
        const hasNewFlags = Number(r.inasistencia1 || 0) > 0 || Number(r.inasistencia2 || 0) > 0;
        if (!hasNewFlags && (Number(r.estado) === 2 || Number(r.estado) === 3)) {
            if (Number(r.estado) === 2) return { backgroundColor: '#fee2e2' };
            if (Number(r.estado) === 3) return { backgroundColor: '#e0f2fe' };
        }
        if (Number(r.estado) === 4 && !hasNewFlags) return { backgroundColor: '#e0f2fe' };

        const getTimeMinutes = (t) => {
            if (!t) return null;
            const s = String(t);
            const timePart = s.includes(' ') ? s.split(' ')[1] : (s.includes('T') ? s.split('T')[1] : s);
            const part = timePart.length >= 5 ? timePart.slice(0, 5) : timePart;
            const parts = part.split(':').map(Number);
            if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;
            return parts[0] * 60 + parts[1];
        };

        const e = getTimeMinutes(r[`entrada${shift}`]);
        const s = getTimeMinutes(r[`scheduled_entrada${shift}`]);

        if (e === null || s === null) return {};

        if (e <= s) return { backgroundColor: '#dcfce7' }; // Verde - puntual
        if (e <= s + (r.tolerancia || 5)) return { backgroundColor: '#dcfce7' }; // Verde - tolerancia
        return { backgroundColor: '#ffedd5' }; // Naranja - tardanza
    };


    const renderEstadoLabel = (r) => {
        if (!r.fromDb) return (
            <span
                className="text-muted"
                style={{
                    cursor: canEditAny ? 'pointer' : 'default',
                    borderBottom: canEditAny ? '1px dashed #9ca3af' : 'none'
                }}
                onClick={() => canEditAny && saveRow(r)}
                title={canEditAny ? "Haga clic para confirmar esta asistencia" : ""}
            >
                PENDIENTE
            </span>
        );

        const est = Number(r.estado);
        const inas1 = Number(r.inasistencia1 || 0);
        const inas2 = Number(r.inasistencia2 || 0);
        const hasSecond = !!r.scheduled_entrada2;

        // ── Inasistencia (estado 2 o 3) ──────────────────────────────────────
        if (est === 2 || est === 3) {
            const t1ConDesc = inas1 === 1;
            const t2ConDesc = inas2 === 1;
            const t1Just = inas1 === 2;
            const t2Just = inas2 === 2;
            const t1Ausente = inas1 > 0;
            const t2Ausente = hasSecond && inas2 > 0;

            // Con nuevos flags: mostrar etiqueta por turno
            if (inas1 > 0 || inas2 > 0) {
                if (hasSecond && t1Ausente && !t2Ausente)
                    return t1ConDesc
                        ? <span className="text-danger" style={{ fontSize: '11px' }}>INASIST. T1 (½ día)</span>
                        : <span className="text-primary" style={{ fontSize: '11px' }}>INASIST. T1 JUSTIF.</span>;

                if (hasSecond && !t1Ausente && t2Ausente)
                    return t2ConDesc
                        ? <span className="text-danger" style={{ fontSize: '11px' }}>INASIST. T2 (½ día)</span>
                        : <span className="text-primary" style={{ fontSize: '11px' }}>INASIST. T2 JUSTIF.</span>;

                // Ambos turnos ausentes
                const todoJust = (t1Just || !t1Ausente) && (t2Just || !t2Ausente);
                if (todoJust)
                    return <span className="text-primary">INASISTENCIA JUSTIF.</span>;
                return <span className="text-danger">INASISTENCIA</span>;
            }

            // Fallback legacy (sin nuevos flags)
            if (est === 3) return <span className="text-primary">INASISTENCIA JUSTIFICADA</span>;
            return <span className="text-danger">INASISTENCIA</span>;
        }

        if (est === 4) return <span className="text-info">TARD. JUSTIF.</span>;

        // ── Normal / tardanza ────────────────────────────────────────────────
        const getTimeMinutes = (t) => {
            if (!t) return null;
            const s = String(t);
            const timePart = s.includes(' ') ? s.split(' ')[1] : (s.includes('T') ? s.split('T')[1] : s);
            const part = timePart.length >= 5 ? timePart.slice(0, 5) : timePart;
            const parts = part.split(':').map(Number);
            if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;
            return parts[0] * 60 + parts[1];
        };

        const e1 = getTimeMinutes(r.entrada1);
        const s1 = getTimeMinutes(r.scheduled_entrada1);
        const e2 = getTimeMinutes(r.entrada2);
        const s2 = getTimeMinutes(r.scheduled_entrada2);

        const checkStatus = (cur, sch) => {
            if (cur === null || sch === null) return 'none';
            if (cur <= sch) return 'puntual';
            if (cur <= sch + (r.tolerancia || 5)) return 'tolerancia';
            return 'tardanza';
        };

        const st1 = checkStatus(e1, s1);
        const st2 = checkStatus(e2, s2);

        if (Number(r.aplica_tardanza) === 1 || Number(r.aplica_tardanza1) === 1 || Number(r.aplica_tardanza2) === 1) {
            return <span className="text-warning">DEDUCCIÓN MINUTOS</span>;
        }

        if (Number(r.justificado1) === 1 && (!r.scheduled_entrada2 || Number(r.justificado2) === 1))
            return <span className="text-info">TARD. JUSTIF.</span>;
        if (Number(r.justificado1) === 1 || Number(r.justificado2) === 1)
            return <span className="text-info" style={{ fontSize: '11px' }}>TARD. PARCIAL JUSTIF.</span>;

        if (st1 === 'tardanza' || st2 === 'tardanza') return <span className="text-danger">1/2 DÍA INASISTENCIA</span>;
        if (st1 === 'tolerancia' || st2 === 'tolerancia') return <span className="text-warning">TARDANZA</span>;
        if (st1 === 'none') return <span className="text-muted">PENDIENTE</span>;

        return <span className="text-success">PUNTUAL</span>;
    };

    const renderTableReporte = () => {
        const users = registrosMensuales.users || [];
        const records = registrosMensuales.records || {};

        return (
            <div className="table-container" style={{ marginTop: '15px' }}>
                <Table bordered hover>
                    <thead>
                        <tr>
                            <th style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>COLABORADOR</th>
                            <th style={{ textAlign: 'center' }}>MINUTOS DE TARDANZA</th>
                            <th style={{ textAlign: 'center' }}>INASISTENCIAS (½ DÍAS)</th>
                            <th style={{ textAlign: 'center' }}>INASISTENCIAS (DÍAS)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length > 0 ? users.map(u => {
                            const stats = u.stats || { totalMinutosTardanza: 0, totalMediodiasFalta: 0, totalFaltas: 0 };

                            return (
                                <tr key={u.usuario_id}>
                                    <td style={{ verticalAlign: 'middle', boxShadow: '1px 0 0 0 #adadad', fontWeight: '500' }}>
                                        {(u.nombre_completo || 'SIN NOMBRE').toUpperCase()}
                                    </td>
                                    <td className="text-center">{stats.totalMinutosTardanza}</td>
                                    <td className="text-center">{stats.totalMediodiasFalta}</td>
                                    <td className="text-center">{stats.totalFaltas}</td>
                                </tr>
                            );
                        }) : (
                            <tr><td colSpan="4" className="text-center text-muted p-4">No hay datos en el mes.</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <div className="caja-flujo-container">
            {isLoading && <Loader />}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h1 style={{ margin: 0 }}>CONTROL DE ASISTENCIAS</h1>
                {canEditAny && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                            color="info"
                            outline
                            style={{ borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => setPerfilesModal(true)}
                            title="Configurar Perfiles"
                        >
                            <Settings size={28} />
                        </Button>
                        <Button
                            color="primary"
                            outline
                            style={{ borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => setConfigAsistenciaModal(true)}
                            title="Asignar Horario"
                        >
                            <UserCog size={28} />
                        </Button>
                    </div>
                )}
            </div>


            <div className="filtros" style={{ opacity: isEditingDisabled ? 0.7 : 1 }}>
                <div className="campo-fecha">
                    <label>Frecuencia:</label>
                    <select className="form-control" value={frecuencia} onChange={(e) => setFrecuencia(e.target.value)}>
                        <option value="diaria">Diariamente</option>
                        <option value="mensual">Mensualmente</option>
                        <option value="reporte">Reporte Mensual</option>
                    </select>
                </div>

                <div className="campo-fecha">
                    <label>{frecuenciaActiva === 'diaria' ? 'Fecha:' : 'Mes:'}</label>
                    {frecuencia === 'diaria' ? (
                        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
                    ) : (
                        <input type="month" value={mesSeleccionado} onChange={(e) => setMesSeleccionado(e.target.value)} />
                    )}
                </div>
                <div className="campo-fecha">
                    <label>Área:</label>
                    <select className="form-control" value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="1">Administración</option>
                        <option value="2">Negocios</option>
                        <option value="4">Gerencia</option>
                    </select>
                </div>
                <div className="campo-fecha">
                    <label>Estado:</label>
                    <select className="form-control" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="1">Activos</option>
                        <option value="0">Cesados</option>
                    </select>
                </div>
            </div>

            <ButtonSearch onClick={() => loadData()} isLoading={isLoading} />

            <div style={{ marginTop: '20px' }}>
                {frecuenciaActiva === 'diaria' ? (
                    <div className="table-container" style={{ marginTop: '15px' }}>
                        <Table>
                            <thead>
                                <tr>
                                    <th rowSpan="2" style={{ minWidth: '130px', verticalAlign: 'middle', boxShadow: '1px 0 0 0 #adadad' }}>COLABORADOR</th>
                                    <th colSpan="2" style={{ textAlign: 'center' }}>TURNO I</th>
                                    <th colSpan="2" style={{ textAlign: 'center' }}>TURNO II</th>
                                    <th rowSpan="2" style={{ width: '120px', minWidth: '120px', textAlign: 'center', verticalAlign: 'middle' }}>ESTADO</th>
                                    {canEditAny && <th rowSpan="2" style={{ width: '72px', minWidth: '72px', textAlign: 'center', verticalAlign: 'middle' }}>ACCIONES</th>}
                                </tr>
                                <tr>
                                    <th style={{ width: '135px', minWidth: '135px', textAlign: 'center' }}>INGRESO</th>
                                    <th style={{ width: '135px', minWidth: '135px', textAlign: 'center' }}>SALIDA</th>
                                    <th style={{ width: '135px', minWidth: '135px', textAlign: 'center' }}>INGRESO</th>
                                    <th style={{ width: '135px', minWidth: '135px', textAlign: 'center' }}>SALIDA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {registros.length > 0 ? registros.map((r) => {
                                    const isFalta = Number(r.estado) === 2;
                                    return (
                                        <tr key={r.usuario_id}>
                                            <td style={{ verticalAlign: 'middle', boxShadow: '1px 0 0 0 #adadad' }}>
                                                <div style={{ fontWeight: '500' }}>{r.nombre_completo.toUpperCase()}</div>
                                                <div className="asistencias-horario-resumen">
                                                    {r.horarioResumen}
                                                </div>
                                            </td>
                                            <td style={getShiftStyle(r, 1)}>
                                                {r.scheduled_entrada1 !== null ? (
                                                    isShiftAbsent(r, 1) ? (
                                                        <div className="text-center" style={{ fontWeight: 'bold' }}>—</div>
                                                    ) : (
                                                        <TimeSelector
                                                            value={r.entrada1}
                                                            onChange={(val) => handleCellChange(r.usuario_id, 'entrada1', val)}
                                                            onBlur={(val) => {
                                                                const newRow = { ...r, entrada1: val };
                                                                saveRow(newRow);
                                                            }}
                                                            disabled={isFalta || !canEditAny || isEditingDisabled}
                                                        />
                                                    )
                                                ) : (
                                                    <div className="text-center text-muted">—</div>
                                                )}
                                            </td>
                                            <td style={getShiftStyle(r, 1)}>
                                                {r.scheduled_entrada1 !== null ? (
                                                    isShiftAbsent(r, 1) ? (
                                                        <div className="text-center" style={{ fontWeight: 'bold' }}>—</div>
                                                    ) : (
                                                        <TimeSelector
                                                            value={r.salida1}
                                                            onChange={(val) => handleCellChange(r.usuario_id, 'salida1', val)}
                                                            onBlur={(val) => {
                                                                const newRow = { ...r, salida1: val };
                                                                saveRow(newRow);
                                                            }}
                                                            disabled={isFalta || !canEditAny || isEditingDisabled}
                                                        />
                                                    )
                                                ) : (
                                                    <div className="text-center text-muted">—</div>
                                                )}
                                            </td>
                                            <td style={getShiftStyle(r, 2)}>
                                                {r.hasSecondBlock ? (
                                                    isShiftAbsent(r, 2) ? (
                                                        <div className="text-center" style={{ fontWeight: 'bold' }}>—</div>
                                                    ) : (
                                                        <TimeSelector
                                                            value={r.entrada2}
                                                            onChange={(val) => handleCellChange(r.usuario_id, 'entrada2', val)}
                                                            onBlur={(val) => {
                                                                const newRow = { ...r, entrada2: val };
                                                                saveRow(newRow);
                                                            }}
                                                            disabled={isFalta || !canEditAny || isEditingDisabled}
                                                        />
                                                    )
                                                ) : (
                                                    <div className="text-center text-muted">—</div>
                                                )}
                                            </td>
                                            <td style={getShiftStyle(r, 2)}>
                                                {r.hasSecondBlock ? (
                                                    isShiftAbsent(r, 2) ? (
                                                        <div className="text-center" style={{ fontWeight: 'bold' }}>—</div>
                                                    ) : (
                                                        <TimeSelector
                                                            value={r.salida2}
                                                            onChange={(val) => handleCellChange(r.usuario_id, 'salida2', val)}
                                                            onBlur={(val) => {
                                                                const newRow = { ...r, salida2: val };
                                                                saveRow(newRow);
                                                            }}
                                                            disabled={isFalta || !canEditAny || isEditingDisabled}
                                                        />
                                                    )
                                                ) : (
                                                    <div className="text-center text-muted">—</div>
                                                )}
                                            </td>

                                            <td className="text-center" style={{ verticalAlign: 'middle', opacity: isEditingDisabled ? 0.6 : 1 }}>
                                                {renderEstadoLabel(r)}
                                            </td>
                                            {canEditAny && (
                                                <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <ButtonUpdate
                                                            onClick={() => !isEditingDisabled && setObsModal({
                                                                isOpen: true,
                                                                userId: r.usuario_id,
                                                                nombreColaborador: r.nombre_completo,
                                                                text: r.observaciones || '',
                                                                estado: r.estado,
                                                                aplicaTardanza: r.aplica_tardanza || 0,
                                                                aplicaTardanza1: r.aplica_tardanza1 || 0,
                                                                aplicaTardanza2: r.aplica_tardanza2 || 0,
                                                                justificado1: r.justificado1 || 0,
                                                                justificado2: r.justificado2 || 0,
                                                                inasistencia1: r.inasistencia1 || 0,
                                                                inasistencia2: r.inasistencia2 || 0,
                                                                justificado_salida1: r.justificado_salida1 || 0,
                                                                justificado_salida2: r.justificado_salida2 || 0,
                                                                hasSecondShift: !!r.scheduled_entrada2
                                                            })}
                                                            disabled={isEditingDisabled}
                                                            style={{ opacity: isEditingDisabled ? 0.5 : 1, cursor: isEditingDisabled ? 'not-allowed' : 'pointer' }}
                                                        />
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                }) : (
                                    <tr><td colSpan={canEditAny ? 7 : 6} className="sin-data">No hay datos.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                ) : frecuenciaActiva === 'mensual' ? (
                    renderTableMensual()
                ) : (
                    renderTableReporte()
                )}
            </div>

            <ModalObservaciones
                isOpen={obsModal.isOpen}
                value={obsModal.text}
                currentState={obsModal.estado}
                aplicaTardanza={obsModal.aplicaTardanza}
                aplicaTardanza1={obsModal.aplicaTardanza1}
                aplicaTardanza2={obsModal.aplicaTardanza2}
                justificado1={obsModal.justificado1}
                justificado2={obsModal.justificado2}
                inasistencia1={obsModal.inasistencia1}
                inasistencia2={obsModal.inasistencia2}
                justificado_salida1={obsModal.justificado_salida1}
                justificado_salida2={obsModal.justificado_salida2}
                nombreColaborador={obsModal.nombreColaborador}
                hasSecondShift={obsModal.hasSecondShift}
                onClose={() => setObsModal({ ...obsModal, isOpen: false })}
                canEdit={true}
                onSave={async (newObs, newEstado, globalAplica, j1, j2, at1, at2, inas1, inas2, js1, js2) => {
                    const row = registros.find(r => r.usuario_id === obsModal.userId);
                    if (row) {
                        const newRow = {
                            ...row,
                            observaciones: newObs,
                            estado: newEstado,
                            aplica_tardanza: globalAplica,
                            aplica_tardanza1: at1,
                            aplica_tardanza2: at2,
                            justificado1: j1,
                            justificado2: j2,
                            inasistencia1: inas1 || 0,
                            inasistencia2: inas2 || 0,
                            justificado_salida1: js1 || 0,
                            justificado_salida2: js2 || 0
                        };
                        await saveRow(newRow);
                    }
                    setObsModal({ ...obsModal, isOpen: false });
                }}
            />
            <ModalPerfilesChecklist
                isOpen={perfilesModal}
                onClose={() => setPerfilesModal(false)}
            />
            <ModalConfiguracionAsistencia
                isOpen={configAsistenciaModal}
                onClose={() => {
                    setConfigAsistenciaModal(false);
                    loadData();
                }}
            />
        </div>
    );
}

export default Asistencias;